import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckCircle, Cancel, UploadFile } from '@mui/icons-material';
import { cancelCheck, checkBundle, commitImport, getCheckReport, listAdjudicators, saveFile } from '../../services/runService';
import { BackToRuns, Card, WizardSteps, fmt, shortDate } from './parts';

// 1 · Upload (wf-run-import-2): one .zip; nothing is written until the commit
const UploadStep = ({ onChecked }) => {
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const input = useRef(null);

    const pick = (picked) => {
        setError(null);
        if (picked && !/\.zip$/i.test(picked.name)) {
            setError('Choose the bundle’s .zip file.');
            return;
        }
        setFile(picked || null);
    };

    const run = async () => {
        setBusy(true);
        setError(null);
        try {
            onChecked(await checkBundle(file));
        } catch (err) {
            setBusy(false);
            setError(err.response?.status === 400
                ? 'This file isn’t a bundle ADAS can read: it isn’t a zip, has no manifest.json, or is too large.'
                : 'Couldn’t run the checks. Try again.');
        }
    };

    return (
        <Card>
            <Box
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
                sx={{
                    border: '2px dashed',
                    borderColor: dragging ? 'primary.main' : 'divider',
                    bgcolor: dragging ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'background.default',
                    borderRadius: 2,
                    p: { xs: 3, sm: 5 },
                    textAlign: 'center',
                    transition: 'border-color 150ms, background-color 150ms',
                }}
            >
                <UploadFile sx={{ fontSize: 40, color: 'text.secondary' }} aria-hidden />
                <Typography variant="body1" sx={{ mt: 1 }}>
                    Drop the bundle (.zip) here, or{' '}
                    <Button variant="text" onClick={() => input.current?.click()} sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', fontSize: 'inherit' }}>browse</Button>
                </Typography>
                <input ref={input} type="file" accept=".zip,application/zip" hidden aria-label="Bundle file" onChange={(e) => pick(e.target.files?.[0])} />
                {file && <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>{file.name} · {fmt(Math.round(file.size / 1024))} KB</Typography>}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    A bundle holds manifest.json and the CSV files it lists. The manifest says which run the bundle belongs to.
                </Typography>
            </Box>
            <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
                Take the bundle from the approved storage only, never from email or Slack: it holds transcripts of classroom talk involving minors (IRB).
            </Alert>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Nothing is written until you commit.</Typography>
                <Button variant="contained" size="large" disabled={!file || busy} onClick={run} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                    Run the checks
                </Button>
            </Box>
        </Card>
    );
};

const CheckRow = ({ check }) => (
    <Box component="li" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', listStyle: 'none' }}>
        {check.passed
            ? <CheckCircle sx={{ color: 'success.main', fontSize: 20, mt: 0.25 }} aria-label="Passed" />
            : <Cancel sx={{ color: 'error.main', fontSize: 20, mt: 0.25 }} aria-label="Failed" />}
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{check.title}</Typography>
            {check.detail && <Typography variant="caption" color={check.passed ? 'text.secondary' : 'error.dark'} sx={{ overflowWrap: 'anywhere' }}>{check.detail}</Typography>}
        </Box>
    </Box>
);

const Fact = ({ label, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 2, py: 0.75 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{children}</Typography>
    </Box>
);

// 2 · Check (wf-run-import 3, 4, 6): the twelve checks, each passed or failed with a detail of file
// names, rows and IDs, never text; what a commit would create; or why it can't be imported
const CheckStep = ({ report, onCancel, onContinue }) => {
    const [downloading, setDownloading] = useState(false);
    const failedCount = report.checks.filter((c) => !c.passed).length;
    const w = report.wouldCreate || {};

    const download = async () => {
        setDownloading(true);
        try {
            saveFile(await getCheckReport(report.checkId), `check-report_${report.externalId || report.checkId}.csv`);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, overflowWrap: 'anywhere' }}>
                        {report.fileName}{report.externalId ? ` · bundle_id ${report.externalId}` : ''}
                    </Typography>
                    <Typography variant="overline" component="h2" color="text.secondary">Checks</Typography>
                    <Box component="ul" sx={{ p: 0, m: 0, mt: 1, display: 'grid', gap: 1.25 }}>
                        {report.checks.map((check) => <CheckRow key={check.id} check={check} />)}
                    </Box>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {report.alreadyImported ? (
                        <>
                            <Typography variant="h5" component="h2">Already imported on {shortDate(report.alreadyImported.committedAt)}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                A bundle with this bundle_id is already in {report.alreadyImported.runName}. Importing it again changes nothing. Study 1’s run has exactly one bundle.
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <Button variant="contained" component={RouterLink} to={`/runs/${encodeURIComponent(report.alreadyImported.runId)}`} sx={{ mt: 3, alignSelf: 'flex-end' }}>
                                Back to the run
                            </Button>
                        </>
                    ) : report.passed ? (
                        <>
                            <Typography variant="overline" component="h2" color="text.secondary">What would be created</Typography>
                            <Fact label="Run">{w.runName} (new)</Fact>
                            <Fact label="Lessons">{fmt(w.lessons)}</Fact>
                            <Fact label="Utterances">{fmt(w.utterances)}</Fact>
                            <Fact label="Original labels">{fmt(w.originalLabels)} (UT and RC)</Fact>
                            <Fact label="Items">{fmt(w.items)}</Fact>
                            <Fact label="Flags">
                                {Object.entries(w.flags || {}).filter(([, n]) => n).map(([flag, n]) => `${flag} ${fmt(n)}`).join(' · ') || '—'}
                            </Fact>
                            <Fact label="Slots">{(w.slots || []).join(', ')} (mapped in the next step)</Fact>
                            <Fact label="Codebook">{w.codebookVersion}</Fact>
                            <Fact label="Reason codes">{w.reasonCodesVersion}</Fact>
                            <Box sx={{ flexGrow: 1 }} />
                            <Alert severity="info" icon={false} sx={{ mt: 3 }}>Nothing has been written yet.</Alert>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, mt: 2 }}>
                                <Button variant="outlined" onClick={onCancel}>Cancel</Button>
                                <Button variant="contained" onClick={onContinue}>Continue: map slots</Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography variant="h5" component="h2">This bundle can’t be imported</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {failedCount} {failedCount === 1 ? 'check' : 'checks'} failed. Nothing has been written. Send the check report to Study 1 and import the corrected bundle.
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                                <Button variant="outlined" onClick={onCancel}>Import another bundle</Button>
                                <Button variant="contained" onClick={download} disabled={downloading}>Download the check report</Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
                                File names, row numbers and IDs only; no transcript text.
                            </Typography>
                        </>
                    )}
                </Card>
            </Grid>
        </Grid>
    );
};

// 3 · Map slots (wf-run-import-5): each of the manifest's slots to its own annotator account, and an
// optional schedule; Commit writes everything in one transaction
const MapSlotsStep = ({ report, onBack, onCommitted }) => {
    const slots = report.wouldCreate?.slots || [];
    const [accounts, setAccounts] = useState(null);
    const [failed, setFailed] = useState(false);
    const [mapping, setMapping] = useState({});
    const [schedule, setSchedule] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        listAdjudicators().then(setAccounts).catch(() => setFailed(true));
    }, []);

    const unmapped = slots.filter((s) => !mapping[s]);
    const used = new Set(Object.values(mapping));

    const commit = async () => {
        setBusy(true);
        setError(null);
        try {
            onCommitted(await commitImport(report.checkId, { slots: slots.map((name) => ({ name, userId: mapping[name] })), schedule: schedule.trim() }));
        } catch (err) {
            setBusy(false);
            const status = err.response?.status;
            setError(status === 404
                ? 'This check has expired (checks are kept 24 hours). Upload the bundle again.'
                : status === 409
                    ? 'The bundle failed a check or was imported meanwhile. Nothing was written.'
                    : err.response?.data?.error || 'Couldn’t commit the import. Nothing was written. Try again.');
        }
    };

    return (
        <Card title="Map Study 1’s slots to annotator accounts">
            {failed && <Alert severity="error">Couldn’t load the annotator accounts.</Alert>}
            <Table size="small" aria-label="Slots">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 100 }}>Slot</TableCell>
                        <TableCell>Annotator account</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {slots.map((slot) => (
                        <TableRow key={slot}>
                            <TableCell sx={{ fontWeight: 700 }}>{slot}</TableCell>
                            <TableCell>
                                <FormControl size="small" sx={{ width: '100%', maxWidth: 360 }}>
                                    <Select
                                        displayEmpty
                                        value={mapping[slot] || ''}
                                        onChange={(e) => setMapping((m) => ({ ...m, [slot]: e.target.value }))}
                                        inputProps={{ 'aria-label': `Account for ${slot}` }}
                                        renderValue={(id) => (id
                                            ? accounts?.find((a) => a.id === id)?.email
                                            : <Box component="span" sx={{ color: 'warning.dark' }}>Choose an annotator account</Box>)}
                                        sx={{ '& fieldset': !mapping[slot] ? { borderColor: 'warning.main' } : {} }}
                                    >
                                        {(accounts || []).map((a) => (
                                            <MenuItem key={a.id} value={a.id} disabled={used.has(a.id) && mapping[slot] !== a.id}>
                                                {a.email}{a.name ? ` · ${a.name}` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                Each slot needs its own annotator account. Study 1’s export names slots, never accounts. Which slot codes which lesson is set in the Assign lessons step, after the commit.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                <TextField
                    label="Schedule (optional)"
                    value={schedule}
                    onChange={(e) => setSchedule(e.target.value)}
                    size="small"
                    inputProps={{ maxLength: 200 }}
                    sx={{ width: 320, maxWidth: '100%' }}
                />
                <Typography variant="caption" color="text.secondary">Shown as typed on each adjudicator’s My queue card.</Typography>
            </Box>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={onBack}>Back</Button>
                <Box sx={{ textAlign: 'right' }}>
                    <Button variant="contained" size="large" disabled={busy || unmapped.length > 0 || !accounts} onClick={commit} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                        Commit import
                    </Button>
                    {unmapped.length > 0 && (
                        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.75 }}>Map {unmapped.join(', ')} to continue</Typography>
                    )}
                </Box>
            </Box>
        </Card>
    );
};

// /runs/import (wf-run-import 2–6, YES-259): Upload · Check · Map slots, then Assign lessons on the
// committed run. A dry run writes nothing; Cancel deletes it.
const ImportWizard = () => {
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [step, setStep] = useState(0);

    const cancel = async () => {
        if (report?.checkId && !report.alreadyImported) {
            cancelCheck(report.checkId).catch(() => {});
        }
        setReport(null);
        setStep(0);
    };

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <BackToRuns />
            <Typography variant="h2" component="h1" sx={{ mt: 0.5 }}>Import a bundle</Typography>
            <WizardSteps active={step} />
            {step === 0 && <UploadStep onChecked={(r) => { setReport(r); setStep(1); }} />}
            {step === 1 && report && <CheckStep report={report} onCancel={cancel} onContinue={() => setStep(2)} />}
            {step === 2 && report && (
                <MapSlotsStep
                    report={report}
                    onBack={() => setStep(1)}
                    onCommitted={(runId) => navigate(`/runs/${encodeURIComponent(runId)}/setup`, { replace: true })}
                />
            )}
        </Box>
    );
};

export default ImportWizard;
